import { Box, Button, Typography } from '@mui/material';
import { omit } from '@npi/buddy-utils';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { personalInfoValidationSchema } from 'src/constants/formValidations/addMerchantStep3';
import { PageHeaderVariant } from 'src/layouts/MainLayout/PageHeader';
import { getStylesByPageHeaderVariant } from 'src/layouts/MainLayout/PageHeader/util';
import PrincipalDetails from 'src/pages/AddMerchantPage/steps/MerchantInformation/components/Principal/components/PrincipalDetails';
import { Principal } from 'src/types/AddMerchant/merchantInfo';

import { showErrorToast } from '../Toasts/toast';

import BillingContactSelector from './components/BillingContactSelector';

export type PersonalInfo = Pick<
    Principal,
    | 'isBillingContact'
    | 'firstName'
    | 'middleNames'
    | 'lastName'
    | 'title'
    | 'dateOfBirth'
    | 'nationality'
    | 'email'
    | 'mobileNumber'
    | 'telephoneNumber'
>;

export type PrincipalType = {
    newPrincipalIndex: number;
    principal: PersonalInfo;
    principals?: PersonalInfo[];
};
interface PrincipalFormProps {
    initialValues: PrincipalType;
    onSubmit: (values: { principal: PersonalInfo }) => void;
    onCancel: () => void;
}

const PrincipalForm = ({ initialValues, onSubmit, onCancel }: PrincipalFormProps) => {
    const { subHeadingStyles } = getStylesByPageHeaderVariant(
        PageHeaderVariant.DASHBOARD,
        'border.primary'
    );

    const validationSchema = Yup.object().shape({
        principal: personalInfoValidationSchema,
    });

    return (
        <Formik
            initialValues={omit(initialValues, ['newPrincipalIndex', 'principals'])}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {(formikProps) => {
                return (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={2} padding="16px 8px">
                            <Typography py={2} sx={subHeadingStyles}>
                                Principal {initialValues.newPrincipalIndex} Contact Details
                            </Typography>
                            <Box display="flex" gap="24px" justifyContent={'space-between'}>
                                <BillingContactSelector
                                    initialValues={initialValues}
                                    formik={formikProps}
                                />
                            </Box>
                            <PrincipalDetails
                                name={'principal'}
                                principal={formikProps.values.principal}
                                formik={formikProps}
                                isEditingDisabled={false}
                            />
                            <Box display="flex" gap={2} pt={5} justifyContent="flex-end">
                                <Button
                                    onClick={onCancel}
                                    variant="main"
                                    className="reset-btn btn-medium"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={async (event) => {
                                        event.preventDefault();
                                        if (
                                            !formikProps.values.principal.isBillingContact &&
                                            initialValues?.principals?.length > 0 &&
                                            initialValues?.principal?.isBillingContact &&
                                            formikProps?.values?.newBillingContact === undefined
                                        ) {
                                            formikProps.setErrors({
                                                newBillingContact: true,
                                            });
                                            showErrorToast('Please select a new billing contact.');
                                            return;
                                        }
                                        if (Object.keys(formikProps.errors).length > 0) {
                                            formikProps.setTouched({
                                                principal: {
                                                    firstName: true,
                                                    lastName: true,
                                                    nationality: true,
                                                },
                                            });
                                            showErrorToast('Please fill in required fields');
                                        } else {
                                            formikProps.handleSubmit();
                                        }
                                    }}
                                    variant="main"
                                >
                                    Confirm
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default PrincipalForm;
